/**
 * Display formatters for the service-request feature.
 */
import { DOC_TYPE_LABELS } from '../constants';

export function formatChatTime(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

export function getDocLabel(documentType: string, fallback?: string): string {
  return (
    DOC_TYPE_LABELS[documentType] ??
    fallback ??
    documentType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  );
}

/**
 * Formats an estimated service-fee range into a display string.
 *   (1800, 2500) -> "₹1,800 – ₹2,500"
 *   (999, 999)   -> "₹999"
 *   (null, 2500) -> "₹2,500"
 * Returns null when neither bound is available.
 */
export function formatFeeRange(
  min: number | null | undefined,
  max: number | null | undefined,
): string | null {
  const fmt = (n: number) => `₹${n.toLocaleString('en-IN')}`;
  if (min != null && max != null) return min === max ? fmt(max) : `${fmt(min)} – ${fmt(max)}`;
  if (min != null) return fmt(min);
  if (max != null) return fmt(max);
  return null;
}

/**
 * Formats the expected turnaround time (TAT) into a display string.
 * Prefers the backend's human-readable `expected_tat` (e.g. "2-5 days");
 * otherwise derives a range from the numeric day bounds.
 *   ("2-5 days", _, _) -> "2-5 days"
 *   (null, 2, 6)       -> "2–6 days"
 *   (null, 3, 3)       -> "3 days"
 *   (null, null, 1)    -> "1 day"
 * Returns null when no TAT information is available.
 */
export function formatTat(
  expected: string | null | undefined,
  min: number | null | undefined,
  max: number | null | undefined,
): string | null {
  const expectedTrimmed = expected?.trim();
  if (expectedTrimmed) return expectedTrimmed;
  const days = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;
  if (min != null && max != null) return min === max ? days(max) : `${min}–${max} days`;
  if (max != null) return days(max);
  if (min != null) return days(min);
  return null;
}
